import { Monitor, Bell, FileText, BarChart3, Code2, Shield, Zap, Database, ArrowRight, Plug, Workflow, Boxes } from "lucide-react";
import { Button } from "@/components/ui/button";
import FadeIn from "@/components/FadeIn";
import SEOHead from "@/components/SEOHead";
import FinalCTA from "@/components/home/FinalCTA";
import { useQuoteModal } from "@/hooks/useQuoteModal";
import dispatcherImg from "@/assets/dispatcher-control-room.webp";

const features = [
  { icon: Monitor, title: "Live shipment tracking", desc: "Real-time GPS on every load. Stop calling the driver — see it on your dashboard." },
  { icon: Bell, title: "Automated notifications", desc: "Pickup, milestone, delay, and delivery alerts via email and SMS — before you need to ask." },
  { icon: FileText, title: "Document management", desc: "BOLs, PODs, invoices — digitized, searchable, and downloadable. No more paperwork chases." },
  { icon: BarChart3, title: "Lane analytics & reporting", desc: "Shipping history, rate trends, cost benchmarks, and carrier performance — all exportable." },
  { icon: Shield, title: "Carrier compliance docs", desc: "Insurance, authority, and operating credentials — stored and accessible for every shipment." },
  { icon: Database, title: "Rate matrix engine", desc: "Proprietary pricing software ensures accurate, competitive rates for every lane." },
];

const integrations = [
  { title: "EDI 204 / 214 / 210", desc: "Standard freight EDI for tenders, status updates, and invoicing." },
  { title: "REST API", desc: "Quote, book, track, and pull documents programmatically." },
  { title: "ERP Connectors", desc: "SAP, Oracle, NetSuite, Microsoft Dynamics — direct connection." },
  { title: "WMS Sync", desc: "Hook into your warehouse management system for automated shipping." },
  { title: "TMS-to-TMS", desc: "Bridge to your existing TMS via API or EDI." },
  { title: "Custom Webhooks", desc: "Real-time event notifications pushed to your systems." },
];

const stats = [
  { val: "<2s", label: "Tracking refresh" },
  { val: "99.97%", label: "Platform uptime" },
  { val: "0", label: "Per-user fees" },
  { val: "24/7", label: "Engineering on-call" },
];

const Technology = () => {
  const { open } = useQuoteModal();

  return (
    <>
      <SEOHead
        title="Freight TMS and Tracking | Varstar Alliance"
        description="Alliance Platform — Varstar's proprietary TMS with live GPS tracking, automated alerts, lane analytics, and EDI/API integrations."
        canonical="/technology"
      >
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Alliance Platform",
          "applicationCategory": "Transportation Management System",
          "operatingSystem": "Web",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD", "description": "Included with Varstar Alliance partnership" },
          "provider": { "@type": "Organization", "name": "Varstar Alliance" }
        })}</script>
      </SEOHead>

      {/* Hero */}
      <section className="relative bg-foreground noise-texture pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute top-0 left-1/3 w-[28rem] h-72 bg-electric/15 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-electric/8 rounded-full blur-[100px] pointer-events-none" />
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle, hsl(var(--electric) / 0.5) 1px, transparent 1px)`,
            backgroundSize: "32px 32px",
          }}
        />

        <div className="container mx-auto px-4 lg:px-8 relative z-10 text-center max-w-3xl">
          <FadeIn>
            <span className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-[0.15em] bg-white/[0.06] text-white/70 border border-white/[0.08] backdrop-blur-xs mb-8">
              <Zap className="w-3.5 h-3.5 text-electric" />
              Alliance Platform
            </span>
            <h1 className="secondary-page-title text-white mb-6">
              See everything.
              <br />
              <span className="gradient-text">Control everything.</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
              Varstar's proprietary TMS — real-time visibility, automated alerts, lane analytics, and the data to make smarter shipping decisions. All in one place.
            </p>
            <Button
              className="bg-electric text-white hover:bg-electric-glow font-semibold px-8 py-6 text-base rounded-full shadow-[0_0_30px_-5px_hsl(var(--electric)/0.5)] hover:-translate-y-0.5 transition-all duration-300 group"
              onClick={() => open({ formType: "contact", ctaSource: "technology_contact" })}
            >
              Get in Touch
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* Stats band on dark */}
      <section className="bg-foreground border-t border-white/[0.04] py-10 noise-texture">
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((s, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <div className="bg-white/[0.04] backdrop-blur-xs border border-white/[0.08] border-l-2 border-l-electric rounded-xl px-5 py-4">
                  <div className="text-2xl lg:text-3xl font-black text-white tracking-tight">{s.val}</div>
                  <div className="text-[11px] uppercase tracking-[0.12em] text-white/70 font-semibold mt-1">{s.label}</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Showcase — split layout with photo */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden border border-border shadow-[0_30px_80px_-20px_hsl(var(--foreground)/0.25)]">
                <img
                  src={dispatcherImg}
                  alt="Varstar dispatch operations center monitoring live freight movements"
                  className="w-full h-full object-cover aspect-[4/3]"
                  width={1200}
                  height={900}
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-foreground/40 via-transparent to-electric/10 pointer-events-none" />
              </div>
              <div className="absolute -bottom-5 right-0 lg:-bottom-7 lg:-right-7 bg-foreground text-white rounded-2xl px-6 py-5 shadow-2xl border border-electric/30 max-w-[200px]">
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/70 font-bold mb-1">Live Now</div>
                <div className="text-3xl font-black text-electric tracking-tight">2,847</div>
                <div className="text-xs text-white/60 font-medium">loads in transit</div>
              </div>
            </div>

            <div
            >
              <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-electric mb-4 block">Built In-House</span>
              <h2 className="section-title text-foreground mb-5">
                Not another off-the-shelf TMS.
              </h2>
              <p className="text-muted-foreground text-[16px] leading-relaxed mb-6">
                Alliance Platform was built by our engineers for our operations team — then opened up to our shippers and agents. That means it actually fits how freight gets moved, and we ship updates the same week you ask for them.
              </p>
              <ul className="space-y-3">
                {[
                  { icon: Workflow, label: "Custom workflows for your lanes, not someone else's" },
                  { icon: Plug, label: "EDI, API, and direct ERP connectors out of the box" },
                  { icon: Boxes, label: "One source of truth — quotes, loads, docs, invoices" },
                ].map((b, i) => {
                  const Icon = b.icon;
                  return (
                    <li
                      key={i}
                      className="flex items-start gap-3"
                    >
                      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-electric/10 flex items-center justify-center mt-0.5">
                        <Icon className="w-4 h-4 text-electric" />
                      </div>
                      <span className="text-foreground font-semibold text-[15px] leading-relaxed pt-1.5">{b.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid — bento-style, breaks 3-up monotony */}
      <section className="py-20 lg:py-24 bg-card border-y border-border">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <div
            className="text-center mb-14"
          >
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-electric mb-4 block">What's Inside</span>
            <h2 className="section-title text-foreground">
              Everything you need.{" "}
              <span className="text-muted-foreground">Nothing you don't.</span>
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto text-[15px]">
              No per-user license fees. No software to install. Included with your Varstar partnership.
            </p>
          </div>

          {/* Bento grid: first card spans 2 cols on lg */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => {
              const Icon = f.icon;
              const featured = i === 0;
              return (
                <div
                  key={i}
                  className={`group relative overflow-hidden rounded-2xl border border-border bg-background p-6 lg:p-7 hover:border-electric/40 hover:shadow-[0_12px_40px_-12px_hsl(var(--electric)/0.18)] hover:-translate-y-0.5 transition-all duration-300 ${
                    featured ? "lg:col-span-2 lg:row-span-1" : ""
                  }`}
                >
                  {featured && (
                    <div className="absolute -top-12 -right-12 w-48 h-48 bg-electric/[0.07] rounded-full blur-3xl pointer-events-none" />
                  )}
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-5">
                      <div className="w-11 h-11 rounded-xl bg-electric/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-electric" />
                      </div>
                      {featured && (
                        <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-electric/80">
                          Most Used
                        </span>
                      )}
                    </div>
                    <h3 className={`font-semibold leading-snug text-foreground mb-2 ${featured ? "text-xl lg:text-2xl" : "text-lg"}`}>
                      {f.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-20 lg:py-24 bg-background">
        <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
          <div
            className="text-center mb-12"
          >
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-electric mb-4 block">Integrations</span>
            <h2 className="section-title text-foreground">
              Plugs into what you already run.
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto text-[15px]">
              Automate tenders, tracking, and invoicing through EDI, REST API, or direct connectors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border rounded-2xl overflow-hidden border border-border">
            {integrations.map((item, i) => (
              <div
                key={i}
                className="bg-card p-6 lg:p-7 hover:bg-muted/40 transition-colors duration-300"
              >
                <div className="flex items-start gap-3">
                  <Code2 className="w-5 h-5 text-electric flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-foreground mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            className="text-center mt-12"
          >
            <p className="text-muted-foreground text-sm mb-5">
              Need a custom integration? Our tech team will work with yours.
            </p>
            <Button
              className="bg-electric text-white hover:bg-electric-glow font-semibold px-8 py-6 text-base rounded-full shadow-[0_0_30px_-5px_hsl(var(--electric)/0.4)] group"
              onClick={() => open({ formType: "contact", ctaSource: "technology_integrations" })}
            >
              Talk to Our Tech Team
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
};

export default Technology;
